import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import palette from 'src/theme/palette';
import useMediaQuery from '@mui/material/useMediaQuery';

const BlogViewModal = ({ open, onClose, blog }) => {
  const isMobile = useMediaQuery('(max-width:600px)');
  if (!blog) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth={isMobile ? false : 'lg'}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          margin: isMobile ? 0 : { xs: 2, sm: 3 },
          maxWidth: isMobile ? '100vw' : '900px',
          height: isMobile ? '100vh' : '90vh'
        }
      }}
      sx={{
        '& .MuiDialog-container': {
          justifyContent: 'center',
          alignItems: 'center'
        }
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: palette.primary.main,
          color: palette.primary.contrastText,
          py: 2,
          position: 'relative',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: 'poppinsSemiBold',
            color: palette.secondary.contrastText
          }}
        >
          Blog Preview - {blog?.title}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: palette.secondary.contrastText,
            display: { xs: 'none', sm: 'inline-flex' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, backgroundColor: palette.background.paper }}>
        <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
          {/* Blog Header with Image */}
          {blog.background_image && (
            <Box sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
              <img
                src={blog.background_image}
                alt={blog.title}
                style={{
                  width: '100%',
                  maxHeight: '400px',
                  objectFit: 'cover',
                  borderRadius: '8px'
                }}
              />
            </Box>
          )}

          {/* Blog Title */}
          <Typography
            variant="h3"
            sx={{
              fontFamily: 'poppinsSemiBold',
              color: palette.primary.titledark,
              mb: 2,
              fontSize: { xs: '2rem', md: '2.5rem' }
            }}
          >
            {blog.title}
          </Typography>

          {/* Blog Meta Info */}
          {/* <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip
              label={`Slug: ${blog.slug || 'N/A'}`}
              variant="outlined"
              size="small"
            />
            <Chip
              label={`Meta Title: ${blog.meta_title ? 'Set' : 'Not Set'}`}
              variant="outlined"
              size="small"
              color={blog.meta_title ? 'success' : 'default'}
            />
            {blog.meta_description && (
              <Chip
                label="Meta Description Set"
                variant="outlined"
                size="small"
                color="info"
              />
            )}
          </Box> */}

          {/* Blog Description/Content */}
          <Box
            sx={{
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                fontFamily: 'poppinsSemiBold',
                marginTop: '16px',
                marginBottom: '8px',
                color: palette.primary.titledark
              },
              '& p': {
                fontFamily: 'poppinsRegular',
                lineHeight: 1.6,
                marginBottom: '16px',
                color: palette.text.primary
              },
              '& img': {
                maxWidth: '100%',
                height: 'auto',
                borderRadius: '8px',
                margin: '16px 0',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.02)',
                  boxShadow: `0 4px 12px ${palette.primary.main}40`
                }
              },
              '& a': {
                color: palette.primary.main,
                textDecoration: 'underline',
                '&:hover': {
                  color: palette.primary.dark
                }
              },
              '& blockquote': {
                borderLeft: `4px solid ${palette.primary.main}`,
                paddingLeft: '16px',
                margin: '16px 0',
                fontStyle: 'italic',
                color: palette.text.secondary
              },
              '& ul, & ol': {
                paddingLeft: '24px',
                marginBottom: '16px'
              },
              '& li': {
                marginBottom: '8px'
              },
              '& table': {
                width: '100%',
                borderCollapse: 'collapse',
                margin: '16px 0'
              },
              '& th, & td': {
                border: `1px solid ${palette.divider}`,
                padding: '8px 12px',
                textAlign: 'left'
              },
              '& th': {
                backgroundColor: palette.background.neutral,
                fontFamily: 'poppinsSemiBold'
              }
            }}
            onClick={(e) => {
              if (e.target.tagName === 'IMG' && window.Fancybox) {
                e.preventDefault();
                window.Fancybox.show([
                  {
                    src: e.target.src,
                    type: 'image'
                  }
                ]);
              }
            }}
            dangerouslySetInnerHTML={{ __html: blog.description }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, backgroundColor: palette.background.neutral }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: palette.primary.main,
            color: palette.primary.contrastText,
            width: { xs: '100%', sm: 'auto' },
            '&:hover': {
              backgroundColor: palette.primary.dark
            }
          }}
        >
          Close Preview
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BlogViewModal;
